import React, { useState, useEffect, useRef } from 'react';

interface TalkingPointSource {
  url: string;
  label: string;
  type: 'web' | 'x';
}

interface TalkingPoint {
  claim: string;
  detail: string;
  sourceType: string;
  sources?: TalkingPointSource[];
  isXSource?: boolean;
  xMeta?: { handle: string; likes: string; reposts?: string };
}

interface DiscourseTab {
  key: string;
  label: string;
  color: string;
  colorLight?: string;
  voiceCount?: number;
  talkingPoints: TalkingPoint[];
}

interface DiscourseSnapshotProps {
  snapshot: {
    tabs: DiscourseTab[];
  };
  keyDisagreement?: string | null;
}

const XLogo: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = '#64748b' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 11,
  color = '#94a3b8'
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

function deriveColorLight(hex: string): string {
  return hex + '10';
}

function isXSourcePoint(point: TalkingPoint): boolean {
  if (point.isXSource) return true;
  return /VIRAL ON X|X DEBATE|X DISCOURSE|FROM X/i.test(point.sourceType);
}

function sortXTakesFirst(points: TalkingPoint[]): TalkingPoint[] {
  return [...points].sort((a, b) => {
    const aIsX = isXSourcePoint(a) ? 0 : 1;
    const bIsX = isXSourcePoint(b) ? 0 : 1;
    return aIsX - bIsX;
  });
}

function TalkingPointCard({
  point,
  tabColor,
  index
}: {
  point: TalkingPoint;
  tabColor: string;
  index: number;
}) {
  const xSource = isXSourcePoint(point);

  return (
    <div
      className="relative overflow-hidden transition-all duration-200 group"
      style={{
        padding: '16px 18px',
        borderRadius: '12px',
        background: xSource
          ? 'linear-gradient(135deg, #f8fafc 0%, rgba(241,245,249,0.5) 100%)'
          : 'white',
        border: xSource ? '1px solid #e2e8f0' : '1px solid #f1f0ee',
        animation: `cardReveal 0.3s ease-out ${index * 0.06}s both`
      }}
    >
      {/* Accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 rounded-l-[3px]"
        style={{
          width: '3.5px',
          background: xSource ? 'linear-gradient(to bottom, #0f172a, #334155)' : tabColor
        }}
      />

      {/* X badge */}
      {xSource && (
        <div
          className="inline-flex items-center gap-1.5 rounded-md mb-2.5"
          style={{ padding: '3px 10px 3px 8px', background: '#0f172a' }}
        >
          <XLogo size={10} color="white" />
          <span
            className="text-[10px] font-extrabold text-white tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            FROM THE CONVERSATION
          </span>
          {point.xMeta?.likes && (
            <span
              className="text-[10px] font-semibold text-slate-400"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              · {point.xMeta.likes} likes
            </span>
          )}
        </div>
      )}

      {/* Claim */}
      <h4
        className="text-[15px] font-extrabold text-[#0f172a] leading-[1.35] mb-1.5"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif", letterSpacing: '-0.01em' }}
      >
        {point.claim}
      </h4>

      {/* Detail */}
      <p
        className="text-[13px] text-[#64748b] leading-[1.55] mb-2.5"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {point.detail}
      </p>

      {/* Source row */}
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <span
          className="text-[9.5px] font-bold text-[#a1a1aa] tracking-[0.07em] uppercase"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {point.sourceType}
        </span>

        {point.sources && point.sources.length > 0 && (
          <div className="flex items-center gap-2">
            {point.sources.map((src, si) => (
              <a
                key={si}
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#94a3b8] hover:text-[#475569] transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {src.type === 'x' ? (
                  <XLogo size={10} color="currentColor" />
                ) : (
                  <LinkIcon size={10} />
                )}
                {src.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DiscourseSnapshot: React.FC<DiscourseSnapshotProps> = ({ snapshot, keyDisagreement }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState(snapshot.tabs[0]?.key || '');
  const [showAll, setShowAll] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  const activeTabData = snapshot.tabs.find((t) => t.key === activeTab) || snapshot.tabs[0];
  const sortedPoints = activeTabData ? sortXTakesFirst(activeTabData.talkingPoints) : [];
  const visiblePoints = showAll ? sortedPoints : sortedPoints.slice(0, 3);
  const hiddenCount = sortedPoints.length - 3;

  useEffect(() => {
    setShowAll(false);
  }, [activeTab]);

  return (
    <div className="max-w-[812px] mx-auto px-4 md:px-0 mb-4">
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardReveal {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .tab-scroll::-webkit-scrollbar { display: none; }
        .tab-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div
        className="bg-white rounded-[16px] border border-[#e7e5e4] overflow-hidden"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/30 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${snapshot.tabs[0]?.color || '#3b82f6'}12, ${snapshot.tabs[Math.floor(snapshot.tabs.length / 2)]?.color || '#7c3aed'}12, ${snapshot.tabs[snapshot.tabs.length - 1]?.color || '#059669'}12)`
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#475569"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <span
              className="text-[14px] font-extrabold text-[#0f172a]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              What's Being Said
            </span>
            <span
              className="text-[10.5px] font-semibold text-[#a1a1aa] tracking-wide"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              SYNTHESIZED FROM NEWS, SOCIAL MEDIA & ANALYSIS
            </span>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 transition-transform duration-250"
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {isExpanded && (
          <div style={{ animation: 'fadeSlideIn 0.25s ease-out' }}>
            {/* Key Disagreement — THE HOOK, above tabs */}
            {keyDisagreement && (
              <div
                className="mx-4 mb-3.5 rounded-xl border border-[#fde68a] p-3.5 flex items-start gap-2.5"
                style={{ background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)' }}
              >
                <div className="w-[26px] h-[26px] rounded-[7px] bg-[#fef08a] flex items-center justify-center shrink-0 mt-0.5">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#a16207"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
                <div>
                  <p
                    className="text-[10px] font-extrabold text-[#a16207] tracking-[0.08em] mb-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    THE CORE QUESTION
                  </p>
                  <p
                    className="text-[15px] font-semibold text-[#78350f] leading-[1.45] m-0"
                    style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
                  >
                    {keyDisagreement}
                  </p>
                </div>
              </div>
            )}

            {/* Tab pills */}
            <div ref={tabsRef} className="tab-scroll flex gap-1.5 px-4 pb-3.5 overflow-x-auto">
              {snapshot.tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                const lightBg = tab.colorLight || deriveColorLight(tab.color);
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="flex items-center gap-1.5 shrink-0 transition-all duration-200"
                    style={{
                      padding: isActive ? '7px 14px 7px 10px' : '7px 12px 7px 10px',
                      borderRadius: '10px',
                      border: isActive ? `1.5px solid ${tab.color}` : '1.5px solid #e7e5e4',
                      background: isActive ? lightBg : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <div
                      className="w-2 h-2 rounded-full transition-colors duration-200"
                      style={{
                        background: isActive ? tab.color : '#d4d4d8',
                        boxShadow: isActive ? `0 0 0 2px ${tab.color}25` : 'none'
                      }}
                    />
                    <span
                      className="text-[12px] whitespace-nowrap transition-colors duration-200"
                      style={{
                        fontWeight: isActive ? 800 : 600,
                        color: isActive ? tab.color : '#71717a',
                        fontFamily: "'Inter', sans-serif"
                      }}
                    >
                      {tab.label}
                    </span>
                    {tab.voiceCount != null && (
                      <span
                        className="text-[10px] font-bold rounded-[5px] transition-all duration-200"
                        style={{
                          color: isActive ? tab.color : '#a1a1aa',
                          background: isActive ? `${tab.color}12` : '#f4f4f5',
                          padding: '1px 6px',
                          fontFamily: "'Inter', sans-serif"
                        }}
                      >
                        {tab.voiceCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Talking points */}
            {activeTabData && (
              <div className="px-4 pb-1.5 flex flex-col gap-2">
                {visiblePoints.map((point, i) => (
                  <TalkingPointCard
                    key={`${activeTab}-${i}`}
                    point={point}
                    tabColor={activeTabData.color}
                    index={i}
                  />
                ))}
              </div>
            )}

            {/* Show more */}
            {!showAll && hiddenCount > 0 && (
              <div className="px-4 pt-1 pb-3.5">
                <button
                  onClick={() => setShowAll(true)}
                  className="w-full py-2.5 rounded-[10px] border-[1.5px] border-dashed border-[#e2e8f0] bg-[#fafafa] flex items-center justify-center gap-1.5 transition-all hover:border-[#d4d4d8] hover:bg-[#f5f5f4] cursor-pointer"
                >
                  <span
                    className="text-[12px] font-bold text-[#71717a]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {hiddenCount} more {hiddenCount === 1 ? 'take' : 'takes'}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#71717a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
            )}

            {(showAll || hiddenCount <= 0) && <div className="h-2.5" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscourseSnapshot;
